// Edges (§2.9, §2.11): ground support and landings, the teeter/ledge/fall classification (a
// carrier going over drops its victim, popped inward), teeter and ledge input, climbing, hand
// stomps, falling with the balloon auto-save, and the KO commit with credit and flags.
// Server-only branches: stomps and the KO commit (§18.2). Everything else also runs for the local
// bean in predict mode.
import { NO_SLOT, TUNE, VOID } from '../constants';
import { EV, KoFlag, emit } from '../events';
import { BS, Cause } from '../ids';
import { MAPS } from '../maps/index';
import { createGroundHit } from '../maps/mapTypes';
import { MODES } from '../modes/index';
import type { Bean, Vec2, World } from '../types';
import { checkBalloonSave } from './balloon';
import { releaseCarry } from './carry';
import { bodyCollides } from './collision';
import type { PressSet } from './input';
import { PARA_FLOOR_INSET, canJump, launchJump } from './movement';
import { STATE_TABLE } from './stateTable';
import { creditOf, isPredicting, isPuppet, isTeammate, setTouch } from './world';

const hit = createGroundHit();
const koSlots: number[] = [];

/** On (or within the airborne threshold of) the ground under the bean. */
export function isGrounded(b: Bean): boolean {
  return b.z - b.groundZ <= TUNE.bean.airborneZ;
}

/**
 * Sends `slot` into FALLING with horizontal velocity `speed` along (outX, outY) and emits FALL with
 * the credit it would carry right now. Leaving LEDGE or CLIMB starts the ledge cooldown.
 */
export function forceFall(w: World, slot: number, outX: number, outY: number, speed: number): void {
  const b = w.beans[slot];
  if (b.state === BS.LEDGE || b.state === BS.CLIMB) b.cdLedge = TUNE.edge.ledgeCooldownTicks;
  const l = Math.hypot(outX, outY);
  b.vel.x = l > 0 ? (outX / l) * speed : 0;
  b.vel.y = l > 0 ? (outY / l) * speed : 0;
  b.vz = Math.min(b.vz, 0);
  b.state = BS.FALLING;
  b.st = 0;
  emit(w, EV.FALL, slot, creditOf(w, b), b.touchCause);
}

function anchorToEdge(w: World, b: Bean): void {
  MAPS[w.cfg.mapId].nearestGround(w, b.pos.x, b.pos.y, hit);
  b.edgePoint.x = hit.x;
  b.edgePoint.y = hit.y;
  b.edgeNormal.x = hit.nx;
  b.edgeNormal.y = hit.ny;
  b.vel.x = b.vel.y = 0;
  b.vz = 0;
  b.st = 0;
}

function enterTeeter(w: World, b: Bean): void {
  anchorToEdge(w, b);
  b.pos.x = b.edgePoint.x;
  b.pos.y = b.edgePoint.y;
  b.z = b.groundZ;
  b.state = BS.TEETER;
  emit(w, EV.TEETER, b.slot, NO_SLOT, 0, 0, b.edgePoint.x, b.edgePoint.y);
}

/** `Bean.escape` while hanging: the drop input is armed once the stick has not pushed away from the platform. */
const LEDGE_DROP_ARMED = 1;

function enterLedge(w: World, b: Bean): void {
  anchorToEdge(w, b);
  b.pos.x = b.edgePoint.x - b.edgeNormal.x * TUNE.edge.hangOut;
  b.pos.y = b.edgePoint.y - b.edgeNormal.y * TUNE.edge.hangOut;
  b.z = b.groundZ + TUNE.edge.hangZ;
  b.escape = 0;
  b.state = BS.LEDGE;
  emit(w, EV.LEDGE, b.slot, NO_SLOT, 0, 0, b.edgePoint.x, b.edgePoint.y);
}

/**
 * Still sliding out of its own lunge (§2.9 key rule: "a lunge never carries you off an edge by
 * itself"): after the 9 lunge ticks the bean is NORMAL at run speed and decelerates over about 0.6 m,
 * which would otherwise carry a whiff that ends near the rim off it at ≥ 2.5 m/s (a ledge hang, or a
 * fall on ledge cooldown). Ticks since the lunge started are 48 − cdShove, read from replicated
 * state so prediction agrees.
 */
function lungeFollowThrough(b: Bean): boolean {
  return b.state === BS.NORMAL && b.cdShove > 0 && TUNE.shove.cooldownTicks - b.cdShove <= TUNE.shove.lungeTicks + TUNE.shove.followTeeterTicks;
}

/**
 * A grounded body over VOID (§2.9 table): lunges (and their follow-through slide) and slow bodies
 * teeter, mid speeds hang, the rest fall. A carrier first drops its victim, popped 0.5 m inward from
 * its edge point (§2.6), and is then classified as an empty-handed bean (so it can catch the ledge,
 * as §2.6's drop list says).
 */
function classifyEdge(w: World, b: Bean): void {
  if (b.state === BS.CARRYING) {
    releaseCarry(w, b, 'edge');
    b.state = BS.NORMAL;
    b.st = 0;
  }
  const s = Math.hypot(b.vel.x, b.vel.y);
  if (STATE_TABLE[b.state].edge === 'teeter' || s < TUNE.edge.teeterMaxSpeed || lungeFollowThrough(b)) enterTeeter(w, b);
  else if (s < TUNE.edge.ledgeMaxSpeed && b.cdLedge === 0 && (b.state === BS.NORMAL || b.state === BS.TUMBLE || b.state === BS.SLIP)) enterLedge(w, b);
  else forceFall(w, b.slot, b.vel.x, b.vel.y, s);
}

function land(w: World, b: Bean, g: number): void {
  if (b.vz < 0) emit(w, EV.LAND, b.slot, NO_SLOT, 0, Math.round(-b.vz * 10), b.pos.x, b.pos.y);
  b.z = b.groundZ = g;
  b.vz = 0;
}

/** Ground support for bodies in states with an edge rule: landing, walking off raised ground, and edges. */
function groundSupport(w: World, b: Bean): void {
  const g = MAPS[w.cfg.mapId].groundAt(w, b.pos.x, b.pos.y);
  const grounded = isGrounded(b) && b.vz <= 0;
  if (g === VOID) {
    if (grounded) classifyEdge(w, b);
    else if (b.z <= b.groundZ && b.vz <= 0) forceFall(w, b.slot, b.vel.x, b.vel.y, Math.hypot(b.vel.x, b.vel.y));
    return;
  }
  // Landing: at or below the ground and not launching up from it (a hop set this tick has vz > 0).
  if (b.z < g || (b.z <= g && b.vz <= 0)) {
    const landed = b.vz < 0;
    land(w, b, g);
    if (landed && b.jumpBufferTicks > 0 && canJump(b)) launchJump(w, b);
    return;
  }
  // Walked off raised ground (a Podium pedestal): coyote time; the +1 covers this tick's timer phase.
  if (grounded && g < b.groundZ - TUNE.bean.airborneZ) b.coyoteTicks = TUNE.jump.coyoteTicks + 1;
  b.groundZ = g;
}

/** A parachute coming down this close to a rim over a hole catches it (lands on the floor there). */
const PARA_CATCH = 2 * TUNE.bean.radius;

function landParachute(w: World, b: Bean): void {
  const map = MAPS[w.cfg.mapId];
  let g = map.groundAt(w, b.pos.x, b.pos.y);
  if (g === VOID && b.z <= b.groundZ && map.nearestGround(w, b.pos.x, b.pos.y, hit).dist <= PARA_CATCH) {
    // The floor fell away in the last moments of the descent: the umbrella catches the rim.
    b.pos.x = hit.x + hit.nx * PARA_FLOOR_INSET;
    b.pos.y = hit.y + hit.ny * PARA_FLOOR_INSET;
    g = map.groundAt(w, b.pos.x, b.pos.y);
  }
  if (g === VOID) {
    if (b.z <= b.groundZ) forceFall(w, b.slot, b.vel.x, b.vel.y, Math.hypot(b.vel.x, b.vel.y));
    return;
  }
  if (b.z > g) return;
  land(w, b, g);
  b.state = BS.NORMAL;
  b.st = 0;
  b.invulnTicks = TUNE.respawn.invulnTicks;
}

function emitSave(w: World, b: Bean): void {
  emit(w, EV.TEETER_SAVE, b.slot, NO_SLOT, b.st >= TUNE.edge.clutchMinTicks ? 1 : 0);
}

function startClimb(w: World, b: Bean): void {
  b.state = BS.CLIMB;
  b.st = 0;
  b.escape = 0;
  b.cdLedge = TUNE.edge.ledgeCooldownTicks;
  emit(w, EV.CLIMB, b.slot);
}

function finishClimb(w: World, b: Bean): void {
  const n = b.edgeNormal;
  b.pos.x = b.edgePoint.x + n.x * TUNE.edge.climbInset;
  b.pos.y = b.edgePoint.y + n.y * TUNE.edge.climbInset;
  const g = MAPS[w.cfg.mapId].groundAt(w, b.pos.x, b.pos.y);
  // groundZ still holds the rim height; if the rim crumbled meanwhile, ground support resolves it next tick.
  b.z = b.groundZ = g === VOID ? b.groundZ : g;
  b.vz = 0;
  b.state = BS.NORMAL;
  b.st = 0;
  b.dizzy = Math.max(0, b.dizzy - TUNE.dizzy.climbReduce);
}

/**
 * Input phase: TEETER recovers on input toward the inward normal or hops inward on Jump; LEDGE
 * climbs on Jump or input toward the normal and drops on input away from it (§2.9). A drop needs a
 * fresh push: a stick still held away from the platform when the bean caught the ledge (it slipped
 * or was carried off while running out) does not drop it until it has been let go of once
 * (`escape` = LEDGE_DROP_ARMED, replicated, so prediction agrees).
 */
export function edgeActions(w: World, b: Bean, p: PressSet, move: Vec2): void {
  const n = b.edgeNormal;
  const along = move.x * n.x + move.y * n.y;
  if (b.state === BS.TEETER) {
    if (p.jump) {
      emitSave(w, b);
      b.state = BS.NORMAL;
      b.st = 0;
      b.vel.x = n.x * TUNE.edge.teeterHopSpeed;
      b.vel.y = n.y * TUNE.edge.teeterHopSpeed;
      launchJump(w, b);
    } else if (along > TUNE.edge.teeterRecoverDot) {
      emitSave(w, b);
      b.state = BS.NORMAL;
      b.st = 0;
      b.vel.x = n.x * TUNE.edge.teeterRecoverSpeed;
      b.vel.y = n.y * TUNE.edge.teeterRecoverSpeed;
    }
  } else if (b.state === BS.LEDGE) {
    if (p.jump || along > TUNE.edge.climbDot) startClimb(w, b);
    else if (along >= TUNE.edge.dropDot) b.escape = LEDGE_DROP_ARMED;
    else if (b.escape === LEDGE_DROP_ARMED) forceFall(w, b.slot, -n.x, -n.y, 0);
  }
}

/**
 * A free body standing on the ground (not launching up from it) whose center reaches the edge point
 * crunches the hands (§2.9), once the hanger's fingers have gripped the rim for `stompGraceTicks`:
 * a shover's post-lunge slide that ends on the hands does not stomp them at once, so the hanger has
 * a moment to climb (the ledge mind-game). A victim popped inward by a carrier that went over is
 * airborne until it lands, too.
 */
function stomps(w: World): void {
  for (const h of w.beans) {
    if (!h.active || h.state !== BS.LEDGE || h.st < TUNE.edge.stompGraceTicks) continue;
    for (const o of w.beans) {
      if (o === h || !bodyCollides(o) || !isGrounded(o) || o.vz > 0) continue;
      if (Math.hypot(o.pos.x - h.edgePoint.x, o.pos.y - h.edgePoint.y) > TUNE.edge.stompRadius) continue;
      if (!isTeammate(w, o.slot, h.slot)) setTouch(w, h.slot, o.slot, Cause.STOMP);
      emit(w, EV.STOMP, o.slot, h.slot, 0, 0, h.edgePoint.x, h.edgePoint.y);
      forceFall(w, h.slot, -h.edgeNormal.x, -h.edgeNormal.y, 0);
      break;
    }
  }
}

function koFlags(w: World, victim: number, credit: number): number {
  if (credit === NO_SLOT) return KoFlag.SELF;
  const c = w.beans[credit];
  let flags = 0;
  if (c.out) flags |= KoFlag.GHOST_KO;
  if (c.koBy === victim) flags |= KoFlag.REVENGE;
  if (victim === w.cfg.crownedSlot) flags |= KoFlag.BOUNTY;
  if (isTeammate(w, credit, victim)) flags |= KoFlag.TEAMKILL;
  return flags;
}

/**
 * KO commit (§2.9): credit and flags, the KO event at the splash point, then GHOST (out, with a
 * shared place for same-tick KOs: beans still in + 1) or RESPAWN in respawning modes, then
 * `MODES.onKO`. Places are frozen once the round is over.
 */
function commitKOs(w: World): void {
  const mode = MODES[w.cfg.modeId];
  for (const v of koSlots) {
    const b = w.beans[v];
    const credit = creditOf(w, b);
    emit(w, EV.KO, v, credit, b.touchCause, koFlags(w, v, credit), b.pos.x, b.pos.y);
    b.koTick = w.tick;
    b.koBy = credit;
    w.koOrder.push(v);
    b.vel.x = b.vel.y = 0;
    b.vz = 0;
    b.z = TUNE.edge.killZ;
    b.st = 0;
    if (mode.respawns) {
      b.state = BS.RESPAWN;
      b.respawnTicks = TUNE.respawn.ticks;
    } else {
      b.state = BS.GHOST;
      b.out = true;
    }
  }
  if (!mode.respawns && !w.over) {
    let stillIn = 0;
    for (const b of w.beans) if (b.active && !b.out) stillIn++;
    for (const v of koSlots) w.beans[v].place = stillIn + 1;
  }
  for (const v of koSlots) mode.onKO(w, v, w.beans[v].koBy, w.beans[v].touchCause);
}

/**
 * Commits a KO for `slot` right now, as if it had reached the kill plane (for mode rules such as
 * Hot Potato's blast, §4.2): credit, flags, place, KO event, GHOST or RESPAWN and `MODES.onKO`.
 * Server-only; a bean that is out, inactive or already waiting to respawn is ignored.
 */
export function commitKO(w: World, slot: number): void {
  const b = w.beans[slot];
  if (isPredicting(w) || !b || !b.active || b.out || b.state === BS.GHOST || (b.state === BS.RESPAWN && b.respawnTicks > 0)) return;
  koSlots.length = 0;
  koSlots.push(slot);
  commitKOs(w);
  koSlots.length = 0;
}

/** Step phase 9: timed edge states, ground support, stomps, the balloon auto-save and KO commits. */
export function stepEdges(w: World): void {
  const server = !isPredicting(w);
  koSlots.length = 0;
  for (const b of w.beans) {
    if (!b.active || isPuppet(w, b.slot)) continue;
    switch (b.state) {
      case BS.TEETER:
        if (b.st >= TUNE.edge.teeterTicks) forceFall(w, b.slot, -b.edgeNormal.x, -b.edgeNormal.y, TUNE.edge.teeterFallSpeed);
        break;
      case BS.LEDGE:
        if (b.st >= TUNE.edge.ledgeTicks) forceFall(w, b.slot, -b.edgeNormal.x, -b.edgeNormal.y, 0);
        break;
      case BS.CLIMB:
        if (b.st >= TUNE.edge.climbTicks) finishClimb(w, b);
        break;
      case BS.FALLING:
        if (checkBalloonSave(w, b)) break;
        if (b.z > TUNE.edge.killZ) break;
        if (server) {
          koSlots.push(b.slot);
        } else {
          // Predict mode: the KO commit is the server's (§18.2). Hold at the kill plane where the
          // server parks a KO'd bean, instead of free-falling until the snapshot arrives.
          b.z = TUNE.edge.killZ;
          b.vz = 0;
          b.vel.x = b.vel.y = 0;
        }
        break;
      case BS.RESPAWN:
        if (b.respawnTicks === 0) landParachute(w, b);
        break;
      default:
        if (STATE_TABLE[b.state].edge !== 'none') groundSupport(w, b);
    }
  }
  if (!server) return;
  stomps(w);
  if (koSlots.length > 0) commitKOs(w);
}
